import NecIcon from "../../../shared/necIcon";
import { hideSpinner, showSpinner } from "../utils";
import "./index.css";

export default class LicenseWizard {
    /** Indicates for what reason the license was not valid. */
    #licenseStatus;

    /**
     * Serial number to be sent to the server when updating the license.
     * @type {string | undefined}
     */
    #serialNumber;

    /**
     * Hardware key.
     * @type {string}
     */
    #hKey = "";

    /** Callback to invoke after license successfully activated after basic login. */
    #successCallback;

    /** Success callback to invoke after license successfully activated after external auth login. */
    #externalAuthSuccessCallback;

    /** Callback to insert the reason why the license activation popup was displayed */
    #errorCallback;

    /** Callback to indicate an error during license activation */
    #loginErrorCallback

    /**
     * @type {HTMLElement | null | undefined}
     */
    #loadingSpinnerContainer;

    /**
     * 
     * @param {string} licenseStatus 
     * @param {() => void} successCallback 
     * @param {(...args: *[]) => void} externalAuthSuccessCallback 
     * @param {() => void | undefined} errorCallback 
     * @param {(error:string, errorId: {errors: string[]}) => void | undefined} loginErrorCallback 
     */
    constructor(licenseStatus, successCallback, externalAuthSuccessCallback, errorCallback, loginErrorCallback) {
        this.#licenseStatus = licenseStatus;
        this.#successCallback = successCallback;
        this.#externalAuthSuccessCallback = externalAuthSuccessCallback;
        this.#errorCallback = errorCallback;
        this.#loginErrorCallback = loginErrorCallback;
    }

    async initialize() {
        $(document)
            .on("input", "#licenseKeyParts input", this.#onInput.bind(this))
            .on("paste", "#licenseKeyParts input", this.#onPaste.bind(this))
            .on("click", "#ActivateLicense", this.#finishLicenseSubmit.bind(this))
            .on("click", ".closeDialog", this.#closeDialog.bind(this))
            .on("keydown", (e) => {
                // PBI 34953: Closing the license popup with the ESC key and reopening it causes the popup to break
                // Although calling #closeDialog on ESC would resolve this,
                // the decision was made not to allow ESC to close the popup.
                if (e.key === "Escape" || e.keyCode === 27) {
                    e.preventDefault();
                    e.stopPropagation();
                };
            });

        await this.#licenseRegistration();
    }

    /**
     * License registration entry method. 
     * Replaces Div with a kendo window for license key transaction.
     */
    async #licenseRegistration() {
        return new Promise((resolve, reject) => {
            const url = BINamespace.getPathUrl() + "/Proxyless/ExecuteLicense";
            $.ajax({
                url: url,
                type: "POST",
                contentType: "application/json; charset=utf-8",
                global: false,
                cache: false,
                success: resolve,
                error: reject
            });
        }).then((view) => {
            const dialogElement = document.createElement("dialog");
            dialogElement.id = "licenseWizard";
            dialogElement.innerHTML = view;

            // Prepend instead of append because the Bitwarden extension messes up the dialog if it's the last element in its container.
            $(document.body).prepend(dialogElement);
            dialogElement.showModal();

            this.#loadingSpinnerContainer = document.getElementById("updateLicenseForm");

            this.#toggleValidityIcon(false);
            if (typeof this.#errorCallback === "function") {
                this.#errorCallback();
            }
        }).catch(() => {
            if (typeof this.#errorCallback === "function") {
                this.#errorCallback();
            }
        });
    }

    /**
     * On paste, breaks pasted 16 char long key in 4 strings. 
     * Validation logics ensues.
     * @param {JQuery.TriggeredEvent} e 
     */
    #onPaste(e) {
        e.preventDefault();

        let pastedText;
        if (window.clipboardData && window.clipboardData.getData) { // IE
            pastedText = window.clipboardData.getData("Text");
        }
        else if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData("text/plain");
        }
        else if (e.originalEvent.clipboardData && e.originalEvent.clipboardData.getData) {
            pastedText = e.originalEvent.clipboardData.getData("text/plain");
        }
        if (pastedText) {
            pastedText = pastedText.replace(/-|\s/g, "");
            let pastedTextArray = [];
            let tempKey = "";
            for (let i = 0; i < 16; i++) {
                if (pastedText.length - 1 < i) {
                    pastedTextArray.push(tempKey);
                    break;
                }
                tempKey += pastedText.charAt(i);
                if (tempKey.length > 3) {
                    pastedTextArray.push(tempKey);
                    tempKey = "";
                }
            }
            $("#licenseKeyParts input").each(function (index) {
                if (!(pastedTextArray.length < index)) {
                    $(this).val(pastedTextArray[index]);
                }
            });
            this.#onInput(e);
        }
    }

    /**
     * 1. Go to next box when first 3 boxes have 4 char
     * 2. when there is 16 chars, validate full key
     * 3. enable and focus on next button if valid key.
     * @param {JQuery.TriggeredEvent} e 
     */
    #onInput(e) {
        e.preventDefault();
        $("#ActivateLicense").addClass("disabled")
            .prop("disabled", true);
        this.#toggleValidityIcon(false);

        const target = e.target;

        if (!(target instanceof HTMLInputElement)) {
            throw new Error("Invalid markup");
        }

        if (!target.validity.valid) {
            // The input is valid when it contains exactly 4 characters.
            return;
        }

        if (target.nextElementSibling instanceof HTMLInputElement) {
            // Current input is complete, go to the next one
            target.nextElementSibling.focus();
        }

        const allInputs = document.getElementById("licenseKeyParts")?.querySelectorAll("input");

        if (!allInputs || allInputs.length !== 4) {
            throw new Error("Invalid markup");
        }

        const fullKey = [...allInputs].map(input => input.value).join("");

        if (fullKey.length !== 16) {
            // License key isn't complete yet, let them finish!
            return;
        }

        this.#serialNumber = fullKey;
        const url = BINamespace.getPathUrl() + "/License/LicenseExists";

        this.#showSpinner();

        $.ajax({
            url: url,
            type: "POST",
            context: this,
            global: false,
            data: {
                serialNumber: this.#serialNumber,
                licenseStatus: this.#licenseStatus
            },
            cache: false,
            /**  @this LicenseWizard */
            success: function (data) {
                const $licenseErrorMsg = $(".licenseErrorMsg");
                $licenseErrorMsg.toggleClass("visible", false);
                $licenseErrorMsg.text("");
                if (data.success) {
                    $("#updateLicenseForm").off("submit").on("submit", this.#onFinish.bind(this, {
                        hardwareKey: this.#hKey,
                        serialNumber: this.#serialNumber,
                        licenseStatus: this.#licenseStatus
                    }));
                    const licenseSection = document.getElementById("licenseAgreementSection");
                    if (licenseSection) {
                        showSpinner(licenseSection);
                    }

                    this.#toggleValidityIcon(true, true);
                    $("#licenseKeyParts").removeClass("error");
                    $("#ActivateLicense").removeClass("disabled").prop("disabled", false);
                }
                else {
                    this.#toggleValidityIcon(true, false);
                    $("#loginBtn").prop("disabled", false);

                    $licenseErrorMsg.toggleClass("visible", true);
                    // license failed, display error message
                    if (data.error) {
                        $("#licenseKeyParts").addClass("error");
                        $licenseErrorMsg.text(BINamespace.HTMLDecode(data.error));
                    }
                    else if (data.Errors) {
                        $("#licenseKeyParts").addClass("error");
                        $licenseErrorMsg.text(BINamespace.HTMLDecode(data.Errors.error.errors[0]));
                    }
                }
            },
            complete: () => this.#hideSpinner()
        });
    }

    /**
     * @param {boolean} show 
     * @param {boolean} [isValid]
     */
    #toggleValidityIcon(show, isValid) {
        const necIcon = document.getElementById("keyValidityIcon");

        if (!(necIcon instanceof NecIcon)) {
            throw new Error("Invalid markup");
        }

        const iconSrc = isValid ? "lucide://check-circle-big" : "lucide://circle-x";

        necIcon.classList.toggle("hidden", !show);
        necIcon.setAttribute("src", iconSrc);
    }
    /**
     * Closes window and login on registration.
     * @param {*} data
     * @param {JQuery.SubmitEvent} e
     */
    #onFinish(data, e) {
        e.preventDefault();

        const $form = $("#updateLicenseForm");

        this.#showSpinner();

        // @ts-expect-error Jquery.attr(string) returns string | undefined, 
        // but TS thinks it returns a JQuery object for some reason.
        $.ajax({
            url: $form.attr("action"),
            type: $form.attr("method"),
            data: data,
            global: false,
            /** @param {*} result */
            success: (result) => {
                if (result.success) {
                    this.#closeDialog();

                    if (!BINamespace.authData) {
                        this.#successCallback();
                        return;
                    }

                    const authData = BINamespace.parseJsonString(BINamespace.authData);
                    $.ajax({
                        url: BINamespace.getPathUrl() + "/Account/ConnectFromExternalAccount",
                        data: JSON.stringify({
                            CP: authData.centralPoint,
                            FlowId: authData.flowId,
                            CentralPointHash: authData.centralPoint,
                            Lang: authData.language,
                            UserName: authData.username,
                            IsIframe: authData.isIframe,
                            SignalRId: authData.signalRId,
                            ProviderId: authData.providerId,
                            ProviderType: authData.providerType,
                            IsRefreshed: authData.isRefreshed,
                            ReturnUrl: authData.returnUrl,
                            IsCalledByLogin: true,
                            ForceMobileMode: authData.forceMobileMode
                        }),
                        type: "POST",
                        contentType: "application/json; charset=utf-8",
                        success: (data) => this.#externalAuthSuccessCallback(data)
                    });
                }
                else {
                    let error = '';
                    let errorId;

                    // license failed, display error message
                    if (result.error) {
                        error = result.error;
                    }
                    else if (result.Errors) {
                        error = result.Errors.error.errors[0];
                        errorId = result.Errors.traceparent;
                        console.log(result.Errors.advancedError.errors[0]);
                    }
                    
                    this.#loginErrorCallback(error, errorId);
                    this.#closeDialog();
                }
            },
            complete: () => this.#hideSpinner()
        });

        return false;
    }

    /**
     * Cancels registration process and closes the dialog.
     */
    #closeDialog() {
        this.#destroy();

        const dialogElement = document.getElementById("licenseWizard");

        if (dialogElement instanceof HTMLDialogElement) {
            dialogElement.close();
            dialogElement.remove();
        }
    }
    /**
     * Submit license form.
     */
    #finishLicenseSubmit() {
        $("#updateLicenseForm").trigger("submit");
    }

    #showSpinner() {
        showSpinner(this.#loadingSpinnerContainer);
    }

    #hideSpinner() {
        hideSpinner(this.#loadingSpinnerContainer);
    }

    #destroy() {
        $(document)
            .off("keyup", "#licenseKeyParts input")
            .off("paste", "#licenseKeyParts input")
            .off("click", "#ActivateLicense")
            .off("click", ".closeDialog")
            .off("keydown");
    }
}